import { useState } from "react";
import { motion } from "framer-motion";
import PlayerCard from "./PlayerCard";

const HITTING_STATS = [
  { key: "avg", label: "AVG", description: "Batting Average" },
  { key: "obp", label: "OBP", description: "On-Base Percentage" },
  { key: "slg", label: "SLG", description: "Slugging Percentage" },
  { key: "ops", label: "OPS", description: "On-Base + Slugging" },
  { key: "fwar", label: "fWAR", description: "FanGraphs Wins Above Replacement" },
  { key: "wrc_plus", label: "wRC+", description: "Weighted Runs Created Plus (FanGraphs)" },
  { key: "hits", label: "H", description: "Total Hits" },
  { key: "doubles", label: "2B", description: "Doubles" },
  { key: "hr", label: "HR", description: "Home Runs" },
  { key: "rbi", label: "RBI", description: "Runs Batted In" },
  { key: "runs", label: "R", description: "Runs Scored" },
  { key: "walks", label: "BB", description: "Walks" },
  { key: "strikeouts", label: "K", description: "Strikeouts" },
  { key: "babip", label: "BABIP", description: "Batting Avg on Balls in Play" },
  { key: "stolen_bases", label: "SB", description: "Stolen Bases" }
];

const PITCHING_STATS = [
  { key: "era", label: "ERA", description: "Earned Run Average" },
  { key: "fip", label: "FIP", description: "Fielding Independent Pitching" },
  { key: "whip", label: "WHIP", description: "Walks + Hits per IP" },
  { key: "wins", label: "W", description: "Wins" },
  { key: "innings_pitched", label: "IP", description: "Innings Pitched" },
  { key: "p_strikeouts", label: "K", description: "Strikeouts" },
  { key: "p_walks", label: "BB", description: "Walks" },
  { key: "k_per_9", label: "K/9", description: "Strikeouts per 9 Innings" },
  { key: "bb_per_9", label: "BB/9", description: "Walks per 9 Innings" },
  { key: "p_babip", label: "BABIP", description: "Batting Avg Against on Balls in Play" },
  { key: "earned_runs", label: "ER", description: "Earned Runs" },
  { key: "avg_against", label: "BAA", description: "Batting Average Against" },
  { key: "saves", label: "SV", description: "Saves" },
  { key: "holds", label: "HLD", description: "Holds" }
];

export default function TeamStatsDashboard({ hitters, pitchers }) {
  const [selectedCategory, setSelectedCategory] = useState("hitting");
  const [selectedStat, setSelectedStat] = useState("ops");

  const stats = selectedCategory === "hitting" ? HITTING_STATS : PITCHING_STATS;
  const players = selectedCategory === "hitting" ? hitters : pitchers;

  const statFieldMap = {
    avg: "avg", hits: "hits", doubles: "doubles",
    obp: "obp", slg: "slg", ops: "ops", hr: "home_runs",
    rbi: "rbi", walks: "walks", runs: "runs",
    strikeouts: "strikeouts", fwar: "fwar", wrc_plus: "wrc_plus",
    babip: "babip", stolen_bases: "stolen_bases",
    fip: "fip", wins: "wins", era: "era",
    whip: "whip", innings_pitched: "innings_pitched",
    p_strikeouts: "strikeouts", p_walks: "walks",
    k_per_9: "k_per_9", bb_per_9: "bb_per_9",
    p_babip: "babip", earned_runs: "earned_runs",
    avg_against: "avg_against", saves: "saves", holds: "holds"
  };

  const field = statFieldMap[selectedStat] || "ops";

  // Lower is better for these stats
  const lowerIsBetter = ["era", "whip", "bb_per_9", "earned_runs", "avg_against", "fip"].includes(selectedStat);

  // Sort and get top 10
  const sortedPlayers = [...players]
    .filter(p => p.stats && p.stats[field] !== undefined && p.stats[field] !== null)
    .sort((a, b) => {
      const aVal = parseFloat(String(a.stats[field]).replace(",", "") || "0");
      const bVal = parseFloat(String(b.stats[field]).replace(",", "") || "0");
      return lowerIsBetter ? aVal - bVal : bVal - aVal;
    })
    .slice(0, 10);

  return (
    <div data-testid="team-stats-dashboard">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-4">
        TEAM STATS DASHBOARD
      </h2>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => { setSelectedCategory("hitting"); setSelectedStat("ops"); }}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            selectedCategory === "hitting"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="category-hitting"
        >
          HITTING
        </button>
        <button
          onClick={() => { setSelectedCategory("pitching"); setSelectedStat("era"); }}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            selectedCategory === "pitching"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="category-pitching"
        >
          PITCHING
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {stats.map((stat) => (
          <button
            key={stat.key}
            onClick={() => setSelectedStat(stat.key)}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border ${
              selectedStat === stat.key
                ? "bg-astros-orange text-white border-astros-orange"
                : "bg-astros-surface text-astros-muted border-astros-border hover:border-astros-orange hover:text-white"
            }`}
            data-testid={`stat-btn-${stat.key}`}
            title={stat.description}
          >
            {stat.label}
          </button>
        ))}
      </div>

      <div className="mb-6 p-4 bg-astros-surface border border-astros-border">
        <h3 className="font-bebas text-xl text-astros-orange">
          TOP 10: {stats.find(s => s.key === selectedStat)?.label || selectedStat.toUpperCase()}
        </h3>
        <p className="text-sm text-astros-muted">
          {stats.find(s => s.key === selectedStat)?.description}
        </p>
      </div>

      <div className="space-y-3">
        {sortedPlayers.map((player, index) => (
          <motion.div
            key={player.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center gap-4"
          >
            <div className={`w-8 h-8 flex items-center justify-center font-bebas text-lg ${
              index < 3 ? "bg-astros-orange text-white" : "bg-astros-surface text-astros-muted border border-astros-border"
            }`}>
              {index + 1}
            </div>

            <div className="flex-1">
              <PlayerCard player={player} type={selectedCategory === "hitting" ? "hitter" : "pitcher"} compact />
            </div>

            <div className="w-24 text-right">
              <p className="font-bebas text-2xl text-white">
                {player.stats[field]}
              </p>
              <p className="text-xs text-astros-muted uppercase">
                {stats.find(s => s.key === selectedStat)?.label}
              </p>
            </div>
          </motion.div>
        ))}

        {sortedPlayers.length === 0 && (
          <p className="text-astros-muted text-center py-8">No data available for this stat</p>
        )}
      </div>
    </div>
  );
}
